import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { UIProvider } from '@/contexts/UIContext';
import AuthPage from '@/pages/AuthPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import NewHomePage from '@/pages/NewHomePage';
import DharmBookHomePage from '@/pages/DharmBookHomePage';
import FeedPage from '@/pages/FeedPage';
import PanditPage from '@/pages/PanditPage';
import PanditProfilePage from '@/pages/PanditProfilePage';
import LiveAartiPage from '@/pages/LiveAartiPage';
import MusicPage from '@/pages/MusicPage';
import VideosPage from '@/pages/VideosPage';
import ProfilePage from '@/pages/ProfilePage';
import NewProfilePage from '@/pages/NewProfilePage';
import CreatorDashboard from '@/pages/CreatorDashboard';
import CreatePostPage from '@/pages/CreatePostPage';
import SavedPostsPage from '@/pages/SavedPostsPage';
import MessagesPage from '@/pages/MessagesPage';
import DiscoverPage from '@/pages/DiscoverPage';
import FlowPage from '@/pages/FlowPage';
import FlowUploadPage from '@/pages/FlowUploadPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import EditProfilePage from '@/pages/EditProfilePage';
// AI Granth 2.0 imports
import AIGranthHomePage from '@/pages/AIGranthHomePage';
import AIGranthBookPage from '@/pages/AIGranthBookPage';
import AIGranthChapterPage from '@/pages/AIGranthChapterPage';
import AIGranthSearchPage from '@/pages/AIGranthSearchPage';
import AIGranthBookmarksPage from '@/pages/AIGranthBookmarksPage';
import GuruChatPage from '@/pages/GuruChatPage';
// Layouts
import FacebookLayout from '@/components/FacebookLayout';
import DharmBookLayout from '@/components/DharmBookLayout';
// Shop imports
import ShopLayout from '@/components/shop/ShopLayout';
import ShopHomePage from '@/pages/shop/ShopHomePage';
import CategoryPage from '@/pages/shop/CategoryPage';
import ProductPage from '@/pages/shop/ProductPage';
import CartPage from '@/pages/shop/CartPage';
import CheckoutPage from '@/pages/shop/CheckoutPage';
import ShopAccountPage from '@/pages/shop/ShopAccountPage';
import ShopWalletPage from '@/pages/shop/ShopWalletPage';
import ShopMenuPage from '@/pages/shop/ShopMenuPage';
import ShopOrdersPage from '@/pages/shop/ShopOrdersPage';
import ShopWishlistPage from '@/pages/shop/ShopWishlistPage';
import ShopAddressesPage from '@/pages/shop/ShopAddressesPage';
import ProductDetailPage from '@/pages/Shop/ProductDetailPage';
// Pandit System imports
import FindPanditPage from '@/pages/pandit/FindPanditPage';
import PanditDetailPage from '@/pages/pandit/PanditDetailPage';
import PanditRegistrationPage from '@/pages/pandit/PanditRegistrationPage';
import PanditDashboardPage from '@/pages/pandit/PanditDashboardPage';
import MyBookingsPage from '@/pages/pandit/MyBookingsPage';
import PanditExamPage from '@/pages/pandit/PanditExamPage';
import PanditVerifyPage from '@/pages/pandit/PanditVerifyPage';
import PanditChatPage from '@/pages/pandit/PanditChatPage';
// Admin imports
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminPanditsPage from '@/pages/admin/AdminPanditsPage';
import AdminPostsPage from '@/pages/admin/AdminPostsPage';
import AdminMediaPage from '@/pages/admin/AdminMediaPage';
import AdminReportsPage from '@/pages/admin/AdminReportsPage';
import AdminTemplesPage from '@/pages/admin/AdminTemplesPage';
import AdminLogsPage from '@/pages/admin/AdminLogsPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminAdminsPage from '@/pages/admin/AdminAdminsPage';
import AdminVedVaaniPage from '@/pages/admin/AdminVedVaaniPage';
import AdminPanditVerificationPage from '@/pages/admin/AdminPanditVerificationPage';
import AdminCommissionPage from '@/pages/admin/AdminCommissionPage';
// Admin Shop imports
import AdminShopProductsPage from '@/pages/admin/AdminShopProductsPage';
import AdminShopCategoriesPage from '@/pages/admin/AdminShopCategoriesPage';
import AdminShopOrdersPage from '@/pages/admin/AdminShopOrdersPage';
import AdminShopCouponsPage from '@/pages/admin/AdminShopCouponsPage';
import AdminShopInventoryPage from '@/pages/admin/AdminShopInventoryPage';
import AdminShopSettingsPage from '@/pages/admin/AdminShopSettingsPage';
// Astrology imports
import AstrologyPage from '@/pages/AstrologyPage';
import KundaliGeneratorPage from '@/pages/KundaliGeneratorPage';
import AstrologersPage from '@/pages/AstrologersPage';
import DailyRashifalPage from '@/pages/DailyRashifalPage';
import KundaliMatchingPage from '@/pages/KundaliMatchingPage';
import RemediesPage from '@/pages/RemediesPage';
import DailyDharmaReadPage from '@/pages/DailyDharmaReadPage';
import CameraPage from '@/pages/CameraPage';
// DharmVivah (Matrimony) imports
import VivahHomePage from '@/pages/VivahHomePage';
import VivahRegisterPage from '@/pages/VivahRegisterPage';
import VivahMatchesPage from '@/pages/VivahMatchesPage';
import VivahProfilePage from '@/pages/VivahProfilePage';
import VivahInterestsPage from '@/pages/VivahInterestsPage';
import VivahChatPage from '@/pages/VivahChatPage';
import VivahConversationsPage from '@/pages/VivahConversationsPage';
import VivahMyProfilePage from '@/pages/VivahMyProfilePage';
// Notifications
import NotificationsPage from '@/pages/NotificationsPage';
import AuthCallback from '@/pages/AuthCallback';
import useWebPush from '@/hooks/useWebPush';
import '@/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Register service worker + subscribe to web push when logged in
  useWebPush(user);

  useEffect(() => {
    // Check user session
    const token = localStorage.getItem('dharmbook_token');
    const userData = localStorage.getItem('dharmbook_user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    
    // Check admin session
    const adminToken = localStorage.getItem('dharmbook_admin_token');
    const adminData = localStorage.getItem('dharmbook_admin');
    if (adminToken && adminData) {
      setAdmin(JSON.parse(adminData));
    }
    
    setLoading(false);

    // Remove Emergent badge that blocks navigation
    const removeBadge = () => {
      const badges = document.querySelectorAll('[class*="emergent"], [id*="emergent"], a[href*="emergent"]');
      badges.forEach(badge => badge.remove());
      
      // Also check for any fixed positioned elements at bottom with "Made with" text
      document.querySelectorAll('div[style*="position: fixed"], div[style*="position:fixed"]').forEach(el => {
        if (el.textContent?.includes('Made with') || el.textContent?.includes('Emergent')) {
          el.remove();
        }
      });
    };

    // Run immediately and also observe for dynamically added elements
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('dharmbook_token');
    localStorage.removeItem('dharmbook_user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="text-4xl font-bold text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <UIProvider>
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* User Auth */}
          <Route path="/auth" element={user ? <Navigate to="/" /> : <AuthPage setUser={setUser} />} />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={admin ? <Navigate to="/admin/dashboard" /> : <AdminLoginPage setAdmin={setAdmin} />} />
          <Route path="/admin" element={admin ? <AdminLayout admin={admin} setAdmin={setAdmin} /> : <Navigate to="/admin/login" />}>
            <Route index element={<Navigate to="/admin/dashboard" />} />
            <Route path="dashboard" element={<AdminDashboard admin={admin} />} />
            <Route path="users" element={<AdminUsersPage admin={admin} />} />
            <Route path="pandits" element={<AdminPanditsPage admin={admin} />} />
            <Route path="posts" element={<AdminPostsPage admin={admin} />} />
            <Route path="media" element={<AdminMediaPage admin={admin} />} />
            <Route path="reports" element={<AdminReportsPage admin={admin} />} />
            <Route path="temples" element={<AdminTemplesPage admin={admin} />} />
            <Route path="logs" element={<AdminLogsPage admin={admin} />} />
            <Route path="settings" element={<AdminSettingsPage admin={admin} />} />
            <Route path="admins" element={<AdminAdminsPage admin={admin} />} />
            <Route path="vedvaani" element={<AdminVedVaaniPage admin={admin} />} />
            <Route path="pandit-verification" element={<AdminPanditVerificationPage admin={admin} />} />
            <Route path="commission" element={<AdminCommissionPage admin={admin} />} />
            {/* DharmShop Admin Routes */}
            <Route path="shop/products" element={<AdminShopProductsPage />} />
            <Route path="shop/categories" element={<AdminShopCategoriesPage />} />
            <Route path="shop/orders" element={<AdminShopOrdersPage />} />
            <Route path="shop/coupons" element={<AdminShopCouponsPage />} />
            <Route path="shop/inventory" element={<AdminShopInventoryPage />} />
            <Route path="shop/settings" element={<AdminShopSettingsPage />} />
          </Route>
          
          {/* DharmShop Routes */}
          <Route path="/shop" element={<ShopLayout user={user} />}>
            <Route index element={<ShopHomePage />} />
            <Route path="category/:categoryId" element={<CategoryPage />} />
            <Route path="search" element={<CategoryPage />} />
            <Route path="product/:productId" element={<ProductPage />} />
            <Route path="cart" element={<CartPage />} />
            <Route path="checkout" element={<CheckoutPage />} />
            <Route path="account" element={<ShopAccountPage />} />
            <Route path="wallet" element={<ShopWalletPage />} />
            <Route path="menu" element={<ShopMenuPage />} />
            <Route path="orders" element={<ShopOrdersPage />} />
            <Route path="wishlist" element={<ShopWishlistPage />} />
            <Route path="addresses" element={<ShopAddressesPage />} />
          </Route>
          
          {/* DharmShop Product Detail - Standalone with own navigation */}
          <Route path="/shop/item/:productId" element={<ProductDetailPage />} />
          
          {/* User Routes - New DharmBook Layout */}
          <Route path="/" element={user ? <DharmBookLayout user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}>
            <Route index element={<DharmBookHomePage user={user} />} />
            {/* Pandit System Routes */}
            <Route path="pandit" element={<FindPanditPage user={user} />} />
            <Route path="pandit/register" element={<PanditRegistrationPage user={user} />} />
            <Route path="pandit/dashboard" element={<PanditDashboardPage user={user} />} />
            <Route path="pandit/bookings" element={<MyBookingsPage user={user} />} />
            <Route path="pandit/exam" element={<PanditExamPage user={user} />} />
            <Route path="pandit/verify" element={<PanditVerifyPage user={user} />} />
            <Route path="pandit/chat" element={<PanditChatPage user={user} />} />
            <Route path="pandit/chat/:chatId" element={<PanditChatPage user={user} />} />
            <Route path="pandit/:panditId" element={<PanditDetailPage user={user} />} />
            {/* Other Routes */}
            <Route path="create" element={<CreatePostPage user={user} />} />
            <Route path="create-post" element={<CreatePostPage user={user} />} />
            <Route path="live" element={<LiveAartiPage />} />
            <Route path="music" element={<MusicPage />} />
            <Route path="videos" element={<VideosPage />} />
            {/* AI Granth 2.0 Routes */}
            <Route path="ai-granth" element={<AIGranthHomePage user={user} />} />
            <Route path="ai-granth/guru" element={<GuruChatPage user={user} />} />
            <Route path="ai-granth/search" element={<AIGranthSearchPage user={user} />} />
            <Route path="ai-granth/bookmarks" element={<AIGranthBookmarksPage user={user} />} />
            <Route path="ai-granth/book/:bookId" element={<AIGranthBookPage user={user} />} />
            <Route path="ai-granth/book/:bookId/chapter/:chapterId" element={<AIGranthChapterPage user={user} />} />
            {/* Legacy redirect */}
            <Route path="granth" element={<Navigate to="/ai-granth" replace />} />
            <Route path="granth/*" element={<Navigate to="/ai-granth" replace />} />
            <Route path="messages" element={<MessagesPage user={user} />} />
            <Route path="messages/:conversationId" element={<MessagesPage user={user} />} />
            <Route path="profile" element={<NewProfilePage user={user} setUser={setUser} />} />
            <Route path="profile/:userId" element={<NewProfilePage user={user} setUser={setUser} />} />
            <Route path="profile/edit" element={<EditProfilePage user={user} setUser={setUser} />} />
            <Route path="settings" element={<SettingsPage user={user} onLogout={handleLogout} />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="saved" element={<SavedPostsPage />} />
            <Route path="discover" element={<DiscoverPage user={user} />} />
            {/* Satsang (Community Groups) - Placeholder for now */}
            <Route path="satsang" element={<DiscoverPage user={user} />} />
            <Route path="flow" element={<FlowPage user={user} />} />
            <Route path="flow/upload" element={<FlowUploadPage user={user} />} />
            <Route path="creator-dashboard" element={<CreatorDashboard user={user} />} />
            {/* Astrology Routes */}
            <Route path="jyotish" element={<AstrologyPage user={user} />} />
            <Route path="jyotish/kundali" element={<KundaliGeneratorPage user={user} />} />
            <Route path="jyotish/astrologers" element={<AstrologersPage user={user} />} />
            <Route path="jyotish/rashifal" element={<DailyRashifalPage user={user} />} />
            <Route path="jyotish/matching" element={<KundaliMatchingPage user={user} />} />
            <Route path="jyotish/remedies" element={<RemediesPage user={user} />} />
            {/* Daily Dharma Full Reading */}
            <Route path="daily-dharma" element={<DailyDharmaReadPage />} />
            {/* Camera */}
            <Route path="camera" element={<CameraPage />} />
            {/* DharmVivah (Matrimony) Routes */}
            <Route path="vivah" element={<VivahHomePage user={user} />} />
            <Route path="vivah/register" element={<VivahRegisterPage user={user} />} />
            <Route path="vivah/matches" element={<VivahMatchesPage user={user} />} />
            <Route path="vivah/profile/:profileId" element={<VivahProfilePage user={user} />} />
            <Route path="vivah/interests" element={<VivahInterestsPage user={user} />} />
            <Route path="vivah/chat" element={<VivahConversationsPage user={user} />} />
            <Route path="vivah/chat/:interestId" element={<VivahChatPage user={user} />} />
            <Route path="vivah/my-profile" element={<VivahMyProfilePage user={user} />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
    </UIProvider>
  );
}

export default App;
